import { makeT } from "app/client/lib/localization";
import { textarea } from "app/client/ui/inputs";
import { cssSettingsPage, cssValueLabel } from "app/client/ui/SettingsLayout";
import { textButton } from "app/client/ui2018/buttons";
import { testId, theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { toggleSwitch } from "app/client/ui2018/toggleSwitch";
import { components, tokens } from "app/common/ThemePrefs";

import {
  BindableValue,
  dom,
  DomContents,
  DomElementArg,
  IDisposableOwner,
  keyframes,
  makeTestId,
  Observable,
  styled,
} from "grainjs";

import type { TopProblem } from "app/client/ui/TopProblems";

export function HidableToggle(
  owner: IDisposableOwner,
  value: Observable<boolean | null>,
  options: { labelId?: string } = {},
) {
  return toggleSwitch(value, {
    args: [dom.hide(use => use(value) === null)],
    inputArgs: [options.labelId ? { "aria-labelledby": options.labelId } : undefined],
  });
}

export const cssPageContainer = styled(cssSettingsPage, `
  &-admin-pages {
    padding: 12px;
    font-size: ${vars.mediumFontSize};
  }
`);

/** Green text for positive/success status values. */
export const cssHappyText = styled("span", `
  color: ${theme.controlFg};
`);

/** Red text for error status values. */
export const cssErrorText = styled("span", `
  color: ${theme.errorText};
`);

/** Orange/amber text for warning/danger status values. */
export const cssDangerText = styled("div", `
  color: ${theme.dangerText};
`);

/** Grey text for status values that are not set up. */
const cssGrayedText = styled("div", `
  color: ${theme.lightText};
`);

/** Status of a row: its pill on the right and its problem. Drawn by buildStatusPill. */
export interface RowStatus {
  // Text of the pill.
  label: string;
  // Color of the text. Without it, the text has the default color.
  level?: "success" | "warning" | "error" | "grayed";
  // Shown when hovering the pill, e.g. the error from the check.
  tooltip?: string;
  // The problem to list on top, if any.
  problem?: TopProblem;
}

// Names used by tests of the admin panel to read the color of a pill.
const pillTestId = makeTestId("test-admin-panel-value-label-");

export function buildStatusPill(
  status: BindableValue<RowStatus>,
  ...args: DomElementArg[]
) {
  return dom.domComputed(status, s => [
    cssValueLabel(
      pillText(s),
      s.tooltip ? { title: s.tooltip } : null,
      pillTestId(pillKind(s)),
      ...args,
    ),
  ]);
}

function pillKind(s: RowStatus): "error" | "danger" | "success" | "neutral" {
  switch (s.level) {
    case "error":
      return "error";
    case "warning":
      return "danger";
    case "success":
      return "success";
    default:
      return "neutral";
  }
}

function pillText(s: RowStatus): DomContents {
  switch (s.level) {
    case "error":
      return cssErrorText(s.label);
    case "warning":
      return cssDangerText(s.label);
    case "success":
      return cssHappyText(s.label);
    case "grayed":
      return cssGrayedText(s.label);
    default:
      return s.label;
  }
}

export const cssTextArea = styled(textarea, `
  color: ${theme.inputFg};
  background-color: ${theme.inputBg};
  border: 1px solid ${theme.inputBorder};
  width: 100%;
  padding: 8px 12px;
  outline: none;
  resize: none;
  border-radius: 3px;

  &::placeholder {
    color: ${theme.inputPlaceholderFg};
  }
`);

export const cssWell = styled("div", `
  color: ${theme.text};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 10px;
  width: 100%;

  &-warning {
    border: 1px solid ${tokens.warningLight};
    --icon-color: ${tokens.warningLight};
  }

  &-error {
    border: 1px solid ${components.errorText};
    --icon-color: ${components.errorText};
  }

  &-success {
    border: 1px solid ${components.widgetBorder};
    --icon-color: ${components.controlFg};
  }

  &-muted {
    border: 1px solid ${components.widgetBorder};
    --icon-color: ${components.lightText};
    color: ${components.lightText};
  }
`);

export const cssIconWrapper = styled("div", `
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 2px;
`);

export const cssWellTitle = styled("div", `
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  margin-bottom: 8px;
`);

export const cssWellContent = styled("div", `
  font-size: ${vars.mediumFontSize};
  line-height: 1.4;
  & > p {
    margin: 0px;
  }
  & > p + p {
    margin-top: 8px;
  }
`);

export const cssFlexSpace = styled("div", `
  flex: 1 1 0;
`);

const cssFadeUp = keyframes(`
  from {
    opacity: 0;
    transform: translateY(12px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`);

/**
 * Styles that fade an element up into place after `delay`, unless the user prefers reduced motion.
 * Put these last in a styled() block: they include a nested rule, and grainjs treats everything
 * after the first nested rule as nested.
 */
export function fadeUp(delay = "0s") {
  return `
    animation: ${cssFadeUp} 0.5s ease ${delay} both;

    @media (prefers-reduced-motion: reduce) {
      & {
        animation: none;
      }
    }
  `;
}

export const cssFadeUpGristLogo = styled("div", `
  background-image: var(--icon-GristLogo);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  height: 48px;
  ${fadeUp()}
`);

export const cssFadeUpHeading = styled("div", `
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
  margin: 16px 0px 8px 0px;
  text-align: center;
  ${fadeUp("0.08s")}
`);

export const cssFadeUpSubHeading = styled("div", `
  color: ${tokens.body};
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 24px;
  text-align: center;
  ${fadeUp("0.16s")}
`);

// --- Shared styles for section components (BaseUrlSection, EditionSection, etc.) ---

/** Flex column container for section content. */
export const cssSectionContainer = styled("div", `
  display: flex;
  flex-direction: column;
  gap: 8px;
`);

/** Description text within a section. */
export const cssSectionDescription = styled("div", `
  font-size: ${vars.mediumFontSize};
  line-height: 1.5;
  margin-bottom: 4px;
`);

export const cssCelebrate = styled("div", `
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 10px;
  background: ${tokens.selectionOpaque};
  border: 1px solid ${tokens.primary};
  & a {
    color: ${tokens.primary};
    font-weight: 600;
    text-decoration: none;
  }
  & a:hover {
    color: ${tokens.primaryMuted};
    text-decoration: underline;
  }
`);

export const cssCelebrateIcon = styled("div", `
  flex: none;
  & > div {
    width: 38px;
    height: 38px;
  }
`);

export const cssCelebrateLead = styled("div", `
  font-weight: 700;
  color: ${tokens.body};
  margin-bottom: 2px;
`);

export const cssCelebrateBody = styled("div", `
  font-size: ${vars.mediumFontSize};
  line-height: 1.5;
  color: ${tokens.body};
  & > p {
    margin: 0px;
  }
  & > p + p {
    margin-top: 8px;
  }
`);

/** Inline status text for collapsed section display. */
export const cssSectionStatusText = styled("span", `
  display: inline-flex;
  align-items: center;
  gap: 4px;
`);

/** Inline row of small buttons (confirm, skip, etc.). */
export const cssSectionButtonRow = styled("div", `
  display: flex;
  gap: 8px;
  align-items: center;
`);

const cssSectionConfirmedRow = styled("div", `
  display: flex;
  align-items: center;
  gap: 8px;
`);

const cssSectionConfirmedPill = styled("span", `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${theme.controlPrimaryBg};
  font-size: ${vars.smallFontSize};
`);

const cssSectionSkippedPill = styled("span", `
  color: ${theme.controlPrimaryBg};
  font-size: ${vars.smallFontSize};
`);

/**
 * Builds the confirmed/skipped status row with a pencil-edit button.
 * Shared by all wizard section components.
 *
 * @param confirmed - observable controlling visibility of the whole row
 * @param onEdit - callback when the pencil is clicked (should reset confirmed)
 * @param options.skipped - if set and true, renders the skipped pill instead
 *   of the "Confirmed" pill
 * @param options.skippedLabel - text for the skipped pill (default: "For later")
 * @param options.testPrefix - prefix for test IDs
 */
export function buildConfirmedRow(
  confirmed: Observable<boolean>,
  onEdit: () => void,
  options: { skipped?: Observable<boolean>; skippedLabel?: string; testPrefix?: string } = {},
) {
  const tid = options.testPrefix ? (id: string) => testId(`${options.testPrefix}-${id}`) : testId;
  return dom.domComputed((use) => {
    if (!use(confirmed)) { return null; }
    const isSkipped = options.skipped ? use(options.skipped) : false;
    return cssSectionConfirmedRow(
      isSkipped ?
        cssSectionSkippedPill(options.skippedLabel || t("For later")) :
        cssSectionConfirmedPill(t("Confirmed")),
      textButton(
        icon("Pencil"),
        dom.on("click", onEdit),
        tid("edit"),
      ),
      tid("confirmed-row"),
    );
  });
}

const t = makeT("AdminPanelCss");
